
import React, { createContext, useState, useContext, ReactNode, useMemo } from 'react';
import { ThemeProvider, createTheme, CssBaseline, GlobalStyles } from '@mui/material';
import { paletteDark, paletteLight } from '../../theme/palette';
import { typography } from '../../theme/typography';
import { getTheme as getPremiumTheme } from '../../theme/theme';

interface ThemeContextType {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Build a single theme by manually merging the premium navy + gold theme
// with the legacy palette (which carries custom tokens — mainMenuNav,
// topbarButton, cardAdd, heading, table — that the sidebar still consumes).
// Manual spread keeps merge behavior obvious and avoids createTheme's
// deep-merge edge cases on `components`.
const buildTheme = (mode: 'light' | 'dark') => {
  const premium = getPremiumTheme(mode);
  const legacyPalette = mode === 'light' ? paletteLight : paletteDark;

  return createTheme({
    ...premium,
    palette: {
      ...legacyPalette,    // custom tokens (mainMenuNav, topbarButton, etc.)
      ...premium.palette,  // navy/gold + mode-aware bg/text — premium wins
    },
    typography: {
      ...typography,
      ...premium.typography,
    },
    components: {
      ...premium.components,
    },
  });
};

export const ThemeProviderWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem('themeMode') : null;
    return saved === 'dark' ? 'dark' : 'light';
  });

  const theme = useMemo(() => buildTheme(mode), [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => {
      const next = prevMode === 'light' ? 'dark' : 'light';
      try { window.localStorage.setItem('themeMode', next); } catch {}
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />   
        <GlobalStyles
          styles={{
            "*": {
              scrollbarWidth: "thin", // Firefox
              // Color: `${theme.palette.primary.main} ${theme.palette.background.paper}`, //  thumb and track color for Firefox
            },
            "*::-webkit-": {
              width: "1px", // Width of the  for Webkit browsers
            },
            "*::-webkit--track": {
              background: theme.palette.background.paper, // Track background color
            },
            "*::-webkit--thumb": {
              backgroundColor: theme.palette.primary.main, // Thumb color
              borderRadius: "10px", // Rounded edges for the thumb
            },
          }}
        />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProviderWrapper');
  }
  return context;
};
